import { CoreSelectSearchProps, FileTypeOption } from "@verity/components/core";
import { useState, useCallback, useMemo } from "react";
import {
	convertDurationToNumber,
	convertNumberToDuration,
	DEFAULT_ASSIGNMENT_MAX_SCORE,
	DEFAULT_NUM_ATTEMPTS,
	getInitialDueDates,
	getInitialLateSubmissionDate,
	getInitialTitle,
	isAssignmentPointsValid,
	mapQuizQuestionToAssignment,
	UNLIMITED_ATTEMPTS,
	validateAssessmentDuration,
} from "../verityAssessmentHelper";
import { Lms, QuizQuestionInput, VerityAssessment } from "@verity/api-client/types";
import { QuizWithProctoringOptions } from "../../assessments-page-helper";
import { extractAssessmentName } from "../../../../../helpers/assessment-name";
import { ScoringStrategy } from "@verity/database/prisma";

function getAssessmentDefaults(lms?: Lms): AssessmentDetailsInitialValues {
	const now = new Date();

	const initialDueDates = getInitialDueDates();
	return {
		selectedCourseId: undefined,
		isSynced: Boolean(lms),
		selectedModuleId: undefined,
		assessmentTitle: getInitialTitle(now),
		assessmentDescription: "",
		assessmentDuration: null,
		startDate: initialDueDates.startDate,
		endDate: initialDueDates.endDate,
		allowLateSubmissions: false,
		lateSubmissionDate: getInitialLateSubmissionDate(initialDueDates.endDate),
		allowMultipleAttempts: true,
		numberOfAttempts: DEFAULT_NUM_ATTEMPTS,
		allowAllFiles: true,
		allowedFileTypes: [],
		showScoreOnSubmission: false,
		assignmentMaxScore: DEFAULT_ASSIGNMENT_MAX_SCORE,
		scoringStrategy: "first",
	};
}

export function buildAssessmentDetailsInitialValues(
	verityAssessment?: VerityAssessment,
	assessment?: QuizWithProctoringOptions,
	lms?: Lms,
	quizQuestions?: QuizQuestionInput[],
): AssessmentDetailsInitialValues {
	const defaults = getAssessmentDefaults(lms);

	const assignmentInfo = mapQuizQuestionToAssignment(quizQuestions);

	return {
		selectedCourseId: assessment?.courseId ?? defaults.selectedCourseId,
		isSynced: verityAssessment?.syncGradebook ?? defaults.isSynced,
		selectedModuleId: verityAssessment?.moduleId ?? defaults.selectedModuleId,
		assessmentTitle: assessment ? extractAssessmentName(assessment) : defaults.assessmentTitle,
		assessmentDescription: verityAssessment?.description ?? defaults.assessmentDescription,
		assessmentDuration: verityAssessment?.duration ?? defaults.assessmentDuration,
		startDate: verityAssessment?.startTime ? new Date(verityAssessment.startTime) : defaults.startDate,
		endDate: verityAssessment?.endTime ? new Date(verityAssessment.endTime) : defaults.endDate,
		allowLateSubmissions: verityAssessment?.allowLateSubmission ?? defaults.allowLateSubmissions,
		lateSubmissionDate: verityAssessment?.lateSubmissionDate
			? new Date(verityAssessment.lateSubmissionDate)
			: defaults.lateSubmissionDate,
		allowMultipleAttempts: verityAssessment?.numAttempts !== 1,
		numberOfAttempts:
			verityAssessment?.numAttempts === 0
				? UNLIMITED_ATTEMPTS
				: (verityAssessment?.numAttempts ?? defaults.numberOfAttempts),
		allowAllFiles: assignmentInfo?.allowAllFiles ?? defaults.allowAllFiles,
		allowedFileTypes: assignmentInfo?.allowedFileTypes ?? defaults.allowedFileTypes,
		showScoreOnSubmission: verityAssessment?.showScoreOnSubmit ?? defaults.showScoreOnSubmission,
		assignmentMaxScore: assignmentInfo?.assignmentMaxScore ?? defaults.assignmentMaxScore,
		scoringStrategy: verityAssessment?.scoringStrategy ?? defaults.scoringStrategy,
	};
}

type AssessmentDetailsInitialValues = {
	selectedCourseId?: number;
	isSynced: boolean;
	selectedModuleId?: string;
	assessmentTitle: string;
	assessmentDescription: string;
	assessmentDuration: number | null;
	startDate: Date;
	endDate: Date;
	allowLateSubmissions: boolean;
	lateSubmissionDate: Date | null;
	allowMultipleAttempts: boolean;
	numberOfAttempts: number;
	allowAllFiles: boolean;
	allowedFileTypes: FileTypeOption[];
	showScoreOnSubmission: boolean;
	assignmentMaxScore: number;
	scoringStrategy: ScoringStrategy;
};

export default function useAssessmentDetails(initialValues: AssessmentDetailsInitialValues) {
	const [courseSearchInput, setCourseSearchInput] = useState<string>("");
	const [selectedCourseId, setSelectedCourseId] = useState<number | undefined>(initialValues.selectedCourseId);
	const [isSyncedWithLms, setIsSyncedWithLms] = useState<boolean>(initialValues.isSynced);
	const [selectedModuleId, setSelectedModuleId] = useState<string | undefined>(initialValues.selectedModuleId);
	const [moduleName, setModuleName] = useState<string>("");

	const [assessmentTitle, setAssessmentTitle] = useState<string>(initialValues.assessmentTitle);
	const [assessmentDescription, setAssessmentDescription] = useState<string>(initialValues.assessmentDescription);
	const [assignmentMaxScore, setAssignmentMaxScore] = useState<number | undefined>(initialValues.assignmentMaxScore);
	const [assignmentMaxScoreInput, setAssignmentMaxScoreInput] = useState<string>(
		String(initialValues.assignmentMaxScore),
	);

	const initialDuration = convertNumberToDuration(initialValues.assessmentDuration);
	const initialHourInput = initialDuration ? String(initialDuration.hour) : "";
	const initialMinInput = initialDuration ? String(initialDuration.minute) : "";

	const [assessmentDuration, setAssessmentDuration] = useState<number | null>(initialValues.assessmentDuration); // in minutes, null means not set
	const [hourInput, setHourInput] = useState<string>(initialHourInput);
	const [minInput, setMinInput] = useState<string>(initialMinInput);
	const [startDate, setStartDate] = useState<Date>(initialValues.startDate);
	const [endDate, setEndDate] = useState<Date>(initialValues.endDate);
	const [allowLateSubmissions, setAllowLateSubmissions] = useState<boolean>(initialValues.allowLateSubmissions);
	const [lateSubmissionDate, setLateSubmissionDate] = useState<Date | null>(initialValues.lateSubmissionDate);

	const [allowMultipleAttempts, setAllowMultipleAttempts] = useState<boolean>(initialValues.allowMultipleAttempts);
	const [numberOfAttempts, setNumberOfAttempts] = useState<number>(initialValues.numberOfAttempts);
	const [allowAllFiles, setAllowAllFiles] = useState<boolean>(initialValues.allowAllFiles);
	const [allowedFileTypes, setAllowedFileTypes] = useState<FileTypeOption[]>(initialValues.allowedFileTypes);
	const [showScoreOnSubmission, setShowScoreOnSubmission] = useState<boolean>(initialValues.showScoreOnSubmission);
	const [scoringStrategy, setScoringStrategy] = useState<ScoringStrategy>(initialValues.scoringStrategy);

	const updateCourseTextInput = useCallback((text: string) => {
		setCourseSearchInput(text);
	}, []);

	const courseSearchProps = useMemo((): CoreSelectSearchProps => {
		return {
			value: courseSearchInput,
			onChange: updateCourseTextInput,
		};
	}, [courseSearchInput, updateCourseTextInput]);

	const selectCourse = useCallback((courseId: number | undefined) => {
		setSelectedCourseId(courseId);
	}, []);

	const toggleLmsSync = useCallback((synced: boolean) => {
		setIsSyncedWithLms(synced);
	}, []);

	const selectModule = useCallback((moduleId: string | undefined) => {
		setSelectedModuleId(moduleId);
	}, []);

	const updateModuleName = useCallback((name: string) => {
		setModuleName(name);
	}, []);

	const updateTitle = useCallback((title: string) => {
		setAssessmentTitle(title);
	}, []);

	const updateDescription = useCallback((description: string) => {
		setAssessmentDescription(description);
	}, []);

	const updateAssessmentDuration = useCallback((hour: string, min: string) => {
		setHourInput(hour);
		setMinInput(min);
		const durationErrors = validateAssessmentDuration({ hour, minute: min });
		if (durationErrors && durationErrors.length > 0) {
			return;
		}

		const duration = convertDurationToNumber({ hour, minute: min });
		setAssessmentDuration(duration);
	}, []);

	const updateMaxScore = useCallback((score: string) => {
		setAssignmentMaxScoreInput(score);
		if (isAssignmentPointsValid(score)) {
			setAssignmentMaxScore(Number(score));
		}
	}, []);

	const resetAssessmentDuration = useCallback(() => {
		setAssessmentDuration(null);
		setHourInput("");
		setMinInput("");
	}, []);

	const selectStartDate = useCallback((date: Date | null) => {
		if (!date) return;
		setStartDate(date);
	}, []);
	const selectEndDate = useCallback((date: Date | null) => {
		if (!date) return;
		setEndDate(date);
	}, []);

	const toggleAllowLateSubmissions = useCallback((allow: boolean) => {
		setAllowLateSubmissions(allow);
	}, []);

	const selectLateSubmissionDate = useCallback((date: Date | null) => {
		setLateSubmissionDate(date);
	}, []);

	const toggleAllowMultipleAttempts = useCallback((allow: boolean) => {
		setAllowMultipleAttempts(allow);
	}, []);

	const updateNumberOfAttempts = useCallback((attempts: number) => {
		setNumberOfAttempts(attempts);
	}, []);

	const toggleAllowAllFiles = useCallback((allow: boolean) => {
		setAllowAllFiles(allow);
		if (allow) {
			setAllowedFileTypes([]);
		}
	}, []);

	const updateAllowedFileTypes = useCallback((types: FileTypeOption[]) => {
		setAllowedFileTypes(types);
	}, []);

	const updateScoringStrategy = useCallback((strategy: ScoringStrategy) => {
		setScoringStrategy(strategy);
	}, []);

	const toggleShowScoreOnSubmission = useCallback((show: boolean) => {
		setShowScoreOnSubmission(show);
	}, []);

	return {
		course: {
			selectedCourseId,
			isSynced: isSyncedWithLms,
			onCourseChange: selectCourse,
			onSyncChange: toggleLmsSync,
			selectedModuleId,
			selectModule,
			moduleName,
			onModuleNameChange: updateModuleName,
			assignmentMaxScore,
			assignmentMaxScoreInput,
			onAssignmentMaxScoreChange: updateMaxScore,
			courseSearchProps,
		},
		generalInfo: {
			assessmentTitle,
			assessmentDescription,
			onTitleChange: updateTitle,
			onDescriptionChange: updateDescription,
		},
		timing: {
			durationHourInput: hourInput,
			durationMinInput: minInput,
			assessmentDuration,
			onAssessmentDurationChange: updateAssessmentDuration,
			resetAssessmentDuration,
			startDate,
			endDate,
			onStartDateChange: selectStartDate,
			onEndDateChange: selectEndDate,
			allowLateSubmissions,
			lateSubmissionDate,
			onAllowLateSubmissionsChange: toggleAllowLateSubmissions,
			onLateSubmissionDateChange: selectLateSubmissionDate,
		},
		submission: {
			allowMultipleAttempts,
			numberOfAttempts,
			allowedFileTypes,
			showScoreOnSubmission,
			scoringStrategy,
			allowAllFiles,
			onAllowAllFilesChange: toggleAllowAllFiles,
			onAllowMultipleAttemptsChange: toggleAllowMultipleAttempts,
			onNumberOfAttemptsChange: updateNumberOfAttempts,
			onAllowedFileTypesChange: updateAllowedFileTypes,
			onShowScoreOnSubmissionChange: toggleShowScoreOnSubmission,
			onScoringStrategyChange: updateScoringStrategy,
		},
	};
}

export type AssessmentDetailsReturn = ReturnType<typeof useAssessmentDetails>;
